// Step 5f (2026-11-03; Ahmed's answer 17, mockup Ro3jT69D7UncSpAvAJV2be, screen "Who gets the money pages"): Admin -> Permissions -> By Person. For one
// person, each page: what their role and department give, what is given to them, and what they get. A grant to a person adds; it never takes away.
// Every change is logged with who made it (server).
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Select, Space, Tag, Typography, message } from 'antd';
import { deletePermission, getPersonAccess, listAllPermissions, listUsers, upsertPermission, PersonAccess as Access } from '../../api';
import ResponsiveTable from '../responsive/ResponsiveTable';

const { Text } = Typography;
const LEVEL: Record<string, { label: string; color?: string }> = { none: { label: 'None' }, read_only: { label: 'Read Only', color: 'gold' }, create: { label: 'Create', color: 'blue' }, full: { label: 'Full', color: 'green' } };
const tag = (l: string) => <Tag color={LEVEL[l]?.color} style={{ margin: 0 }}>{LEVEL[l]?.label || l}</Tag>;
const RANK: Record<string, number> = { none: 0, read_only: 1, create: 2, full: 3 };
type Row = Access['rows'][number];

const PersonAccess: React.FC<{ resourceLabel: (key: string) => string }> = ({ resourceLabel }) => {
  const [users, setUsers] = useState<Array<{ id: number; label: string }>>([]);
  const [who, setWho] = useState<number | null>(null);
  const [data, setData] = useState<Access | null>(null);
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    listUsers().then((list: any[]) => setUsers((list || []).filter((u) => u.isActive !== false).map((u) => ({ id: u.id, label: `${u.displayName}${u.department?.name ? ` · ${u.department.name}` : ''} · ${u.role}` })))).catch(() => setUsers([]));
  }, []);
  const load = async (id: number) => { try { setData(await getPersonAccess(id)); setDraft({}); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not load'); } };
  useEffect(() => { if (who) load(who); }, [who]);
  const changes = useMemo(() => Object.entries(draft).filter(([k, v]) => (data?.rows.find((r) => r.resource === k)?.given ?? null) !== v), [draft, data]);
  const levelOf = (r: Row) => (r.resource in draft ? draft[r.resource] : r.given);
  const save = async () => {
    if (!who || !data) return;
    setSaving(true);
    try {
      const mine = await listAllPermissions({ grantType: 'user', grantValue: String(who) });
      for (const [resource, level] of changes) {
        if (level) await upsertPermission({ resource, accessLevel: level, grantType: 'user', grantValue: String(who) });
        else { const row = (mine || []).find((p: any) => p.resource === resource); if (row) await deletePermission(row.id); }
      }
      message.success(`Saved ${changes.length} change${changes.length === 1 ? '' : 's'}`);
      await load(who);
    } catch (e: any) { message.error(e?.response?.data?.error || 'Could not save'); } finally { setSaving(false); }
  };
  return (
    <Card size="small" title={(
      <Space wrap><Text strong>Person</Text>
        <Select showSearch value={who ?? undefined} onChange={setWho} placeholder="Choose a person" style={{ width: 320 }} aria-label="Person"
          options={users.map((u) => ({ value: u.id, label: u.label }))} filterOption={(input, o) => String(o?.label || '').toLowerCase().includes(input.toLowerCase())} />
      </Space>
    )}>
      {data && (
        <>
          <ResponsiveTable<Row>
            rowKey="resource" size="small" pagination={false} dataSource={data.rows}
            columns={[
              { title: 'Page', key: 'page', render: (_: any, r: Row) => resourceLabel(r.resource) },
              { title: 'From role and department', key: 'from', width: 190, render: (_: any, r: Row) => tag(r.fromRoleAndDepartment) },
              {
                title: 'Given to this person', key: 'given', width: 190,
                render: (_: any, r: Row) => (
                  <Select size="small" value={levelOf(r) ?? 'inherit'} style={{ width: 150 }} aria-label={`Given to ${data.user.name} on ${resourceLabel(r.resource)}`}
                    onChange={(x) => setDraft((d) => ({ ...d, [r.resource]: x === 'inherit' ? null : x }))}
                    options={[{ value: 'inherit', label: 'Inherit' }, { value: 'read_only', label: 'Read Only' }, { value: 'create', label: 'Create' }, { value: 'full', label: 'Full' }]} />
                ),
              },
              {
                title: `What ${data.user.name} gets`, key: 'gets', width: 170,
                render: (_: any, r: Row) => { const v = levelOf(r); return tag((RANK[v || 'none'] || 0) > (RANK[r.fromRoleAndDepartment] || 0) ? v! : r.fromRoleAndDepartment); },
              },
            ]}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>A grant to a person adds to what their role and department give. It never takes away. Every change is logged with who made it.</Text>
            <Space>
              <Button disabled={!changes.length} onClick={() => setDraft({})}>Discard</Button>
              <Button type="primary" disabled={!changes.length} loading={saving} onClick={save}>{changes.length ? `Save ${changes.length} change${changes.length === 1 ? '' : 's'}` : 'Save'}</Button>
            </Space>
          </div>
        </>
      )}
    </Card>
  );
};

export default PersonAccess;
